import type { MaybeRefOrGetter } from 'vue';
import { storeToRefs } from 'pinia';
import { computed, onScopeDispose, toValue } from 'vue';
import { useAuth } from '#layers/access/stores/auth';
import { useWorkspaceEntityStore } from '#layers/runtime/public';

export interface UseLastAdminPolicyCheckOptions {
	policyId: MaybeRefOrGetter<string | null | undefined>;
	isAdmin: MaybeRefOrGetter<boolean>;
}

/**
 * Determines if disabling the admin toggle on the given policy would lock out the current user.
 */
export function useLastAdminPolicyCheck(options: UseLastAdminPolicyCheckOptions) {
	const { policyId, isAdmin } = options;
	const { currentUser } = storeToRefs(useAuth());
	const store = useWorkspaceEntityStore();

	const currentUserId = computed(() => currentUser.value?.id ?? null);
	const editingPolicyId = computed(() => toValue(policyId));
	const policyIsAdmin = computed(() => toValue(isAdmin));

	const memberQuery = store.MonospaceSystemUser.query((q) => q.first({
		filter: { id: { _eq: currentUserId.value! } },
		include: { roles: { include: { role: { include: { policies: { include: { policy: {} } } } } } } },
		enabled: !!currentUserId.value,
	}));

	const { data: currentUserMember } = memberQuery;

	// Refresh when a policy somewhere else is changed
	// (to avoid flickering)
	const offAfterMutation = store.$hooks.hook('afterMutation', ({ collection, mutation }) => {
		if (collection.name === 'MonospacePolicy' && ['create', 'update', 'delete'].includes(mutation)) {
			memberQuery.refresh();
		}
		else if (collection.name === 'MonospaceRolePolicy' && ['create', 'delete'].includes(mutation)) {
			memberQuery.refresh();
		}
	});

	onScopeDispose(offAfterMutation);

	const adminPolicyIdsForCurrentUser = computed(() => {
		const member = currentUserMember.value;
		const adminPolicyIds = (member?.roles ?? [])
			.flatMap((ur) => ur.role?.policies ?? [])
			.filter((rp) => (rp.policy?.content as { access?: string })?.access === 'admin')
			.map((rp) => rp.policy?.id);
		return adminPolicyIds;
	});

	const wouldLockOut = computed(() => {
		if (!editingPolicyId.value || !policyIsAdmin.value) return false;
		const adminIds = adminPolicyIdsForCurrentUser.value;
		return adminIds.length === 1 && adminIds[0] === editingPolicyId.value;
	});

	return { wouldLockOut };
}
